/**
 * Concierge chat store — sessions list, turns, streaming state.
 *
 * Mirrors the user-isolation pattern from `useZinsserStore` (cross-user
 * data-leak guard before Zustand hydration). Persisted to localStorage so
 * the active session survives a refresh.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  ConciergeSessionListItem,
  ConciergeToolCall,
  ConciergeToolResult,
} from '@/lib/api'
import {
  listConciergeSessions,
  getConciergeSession,
  updateConciergeSession,
  startConciergeWalk,
  getNextConciergeWalk,
  type ConciergeSessionDetail,
} from '@/lib/api'

/** Pinned sessions first, otherwise preserve the incoming order (which the
 * backend sorts by last_message_at desc). Stable on modern engines. */
function sortPinnedFirst(
  sessions: ConciergeSessionListItem[]
): ConciergeSessionListItem[] {
  return [...sessions].sort((a, b) =>
    a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1
  )
}

// =============================================================================
// CRITICAL: User isolation — runs SYNCHRONOUSLY before store hydration.
// Same pattern as useZinsserStore. If the Supabase auth-token user_id no
// longer matches the user_id we stored last time, wipe the persisted store
// so user A's sessions never appear under user B's account on the same
// browser. This module-load side effect is intentional.
// =============================================================================

const CONCIERGE_USER_ID_KEY = 'concierge-store-user-id'
const CONCIERGE_STORE_KEY = 'concierge-store-v1'

function clearStoreIfUserMismatch(): void {
  try {
    const storedUserId = localStorage.getItem(CONCIERGE_USER_ID_KEY)
    const hasStore = localStorage.getItem(CONCIERGE_STORE_KEY) !== null

    let currentUserId: string | null = null
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        try {
          const raw = localStorage.getItem(key)
          const authData = JSON.parse(raw || '{}')
          currentUserId = authData?.user?.id || authData?.session?.user?.id || null
          if (currentUserId) break
        } catch {
          /* skip invalid JSON */
        }
      }
    }

    if (storedUserId && currentUserId && storedUserId !== currentUserId) {
      localStorage.removeItem(CONCIERGE_STORE_KEY)
      localStorage.setItem(CONCIERGE_USER_ID_KEY, currentUserId)
    } else if (!storedUserId && hasStore && currentUserId) {
      localStorage.removeItem(CONCIERGE_STORE_KEY)
      localStorage.setItem(CONCIERGE_USER_ID_KEY, currentUserId)
    } else if (!storedUserId && currentUserId) {
      localStorage.setItem(CONCIERGE_USER_ID_KEY, currentUserId)
    } else if (!currentUserId && hasStore) {
      localStorage.removeItem(CONCIERGE_STORE_KEY)
    }
  } catch {
    /* localStorage may be unavailable; silent failure is fine */
  }
}
clearStoreIfUserMismatch()


// =============================================================================
// Types
// =============================================================================

export interface ConciergeToolCallRecord {
  /** Client-side sequential id. There's no id on the SSE wire — backend
   * yields tool_call then tool_result in strict order. We assign an id
   * on the client just for React keys + matching. */
  id: string
  name: string
  input: Record<string, unknown>
  /** Backend-formatted result summary (e.g. "3 row(s)", "ok",
   * "completed"). Stored verbatim and rendered as-is by the pill. */
  summary?: string
  /** running → success | error */
  status: 'running' | 'success' | 'error'
}

export interface Turn {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  thinking?: string
  toolCalls?: ConciergeToolCallRecord[]
}

export type WorkflowStatus = 'idle' | 'submitting' | 'streaming' | 'error'

/**
 * Which workflow is "playing" inside the chat shell. Each non-default
 * mode talks to a different streaming endpoint, mints its own backend
 * session (so the persisted `mode` column on `concierge_session_turns`
 * stays accurate), and gets a sticky banner + per-turn accent so the
 * user always knows which "room" they're in.
 */
export type ChatMode = 'default' | 'wrapup' | 'prep' | 'review' | 'penn_review'

/**
 * Per-mode context the agent needs and the banner needs to display.
 * Carried alongside `chatMode` so consumers don't have to rummage
 * through URL params or props on every render.
 */
export interface ModeContext {
  /** Absolute folder path. Required for wrapup + prep; optional for
   *  review (where a project-scoped review narrows the rollup window). */
  blockPath?: string
  /** Required for review when not scoping by block. Project folder
   *  path, e.g. /Users/lovell_armand/_notes/penn/PENN Inbox. */
  projectPath?: string
  /** Required for review. Picks the variant the backend runs. */
  reviewVariant?: 'daily' | 'weekly' | 'ninety_day'
  /** Wrap-up only. When true (set by the calendar wrap-up link), the
   *  auto-greet first turn runs the agent AUTONOMOUSLY (headless) instead of
   *  the interactive evidence-review opener — it writes the outcome + wrap-up
   *  card + CONCIERGE.md and archives, streamed. The user then refines in
   *  normal conversation on the same session. Unset for badge/walk entries,
   *  which keep the interactive opener. */
  autoRun?: boolean
  /** Penn Review only. ISO YYYY-MM-DD window. Both required when
   *  mode === 'penn_review'. */
  dateFrom?: string
  dateTo?: string
  /** Penn Review only. Optional emphasis note passed to per-workstream agents. */
  contextNote?: string
  /** Penn Review only. If false, the card stays in chat and isn't written
   *  to penn/Penn Review/. Default true. */
  writeToVault?: boolean
  /** Display title in the banner. The agent doesn't see this — purely
   *  cosmetic. Pulled from the wrap-up trigger card or derived from
   *  the block_path / project_path. */
  displayTitle?: string
  /** Second-line context under the displayTitle in the banner. Format
   *  mirrors the calendar's LOCATION string: `<domain> - [<parent> -]
   *  <workstream> - <timestamp>`. Same cosmetic-only treatment as
   *  displayTitle. Optional; omit and the banner stays single-line. */
  displaySubtitle?: string
}


// =============================================================================
// Store
// =============================================================================

interface ConciergeStore {
  // Session list
  sessions: ConciergeSessionListItem[]
  sessionsLoading: boolean

  // Active session
  sessionId: string | null
  turns: Turn[]

  // Mode (default chat vs. wrap-up / prep / review sub-workflow)
  chatMode: ChatMode
  modeContext: ModeContext | null

  // Auto-greet arming. The chat's auto-greet effect fires an empty-content
  // first turn when it sees a non-default mode with no session yet. That
  // condition is ALSO true after a page reload rehydrates a persisted
  // wrap-up mode — which silently re-ran (and, with `autoRun` persisted,
  // headlessly archived) a STALE block the user wasn't looking at (the
  // 2026-06-01 "Monitor Meadows banner / Pay Bills wrap-up" incident).
  // This flag is set ONLY by enterMode (an in-session, intentional entry)
  // and is NOT persisted, so a rehydrated mode never auto-fires. The
  // auto-greet effect consumes it via disarmAutoGreet().
  autoGreetArmed: boolean

  // Stashed default-mode state, restored on exitMode(). We snapshot
  // sessionId + turns when entering a non-default mode so the user
  // returns to their previous conversation instead of a blank slate.
  // Streaming state isn't snapshotted — it's transient by definition;
  // a mode switch mid-stream would also be unusual UX.
  stashedSessionId: string | null
  stashedTurns: Turn[]

  // Streaming state (transient — not persisted)
  workflowStatus: WorkflowStatus
  streamingThinking: string
  streamingToolCalls: ConciergeToolCallRecord[]
  streamingContent: string

  error: string | null

  // ── Session lifecycle ──
  loadSessions: () => Promise<void>
  loadSession: (sessionId: string) => Promise<void>
  newSession: () => void
  togglePin: (sessionId: string) => Promise<void>

  // ── Mode lifecycle ──
  enterMode: (mode: Exclude<ChatMode, 'default'>, context: ModeContext) => void
  exitMode: () => void
  // Consumed by the chat's auto-greet effect after it fires, so the greet
  // happens exactly once per intentional mode entry.
  disarmAutoGreet: () => void

  // ── Wrap-up walks ──
  // `walkActive` flips on when startWalk lands the first block and stays on
  // until exitMode() or vault_cleared. The chat watches it to know whether
  // a terminal tool result should trigger advanceWalk().
  // `walkUniverse` persists the universe filter (set when startWalk fires
  // with one) so subsequent advances pass it to /walks/next, keeping the
  // walk constrained to that universe instead of crossing into others.
  walkActive: boolean
  walkUniverse: string | null
  startWalk: (universe?: string) => Promise<void>
  advanceWalk: () => Promise<void>

  // ── Per-turn streaming actions ──
  appendUserTurn: (content: string) => void
  startStreaming: () => void
  appendThinking: (delta: string) => void
  finishThinking: (final?: string) => void
  registerToolCall: (call: ConciergeToolCall) => void
  registerToolResult: (result: ConciergeToolResult) => void
  appendStreamingContent: (delta: string) => void
  finishStreaming: (final: { content: string; sessionId?: string }) => void
  setError: (err: string | null) => void
  reset: () => void
}

const initialStreamingState = {
  workflowStatus: 'idle' as WorkflowStatus,
  streamingThinking: '',
  streamingToolCalls: [] as ConciergeToolCallRecord[],
  streamingContent: '',
}

export const useConciergeStore = create<ConciergeStore>()(
  persist(
    (set, get) => ({
      sessions: [],
      sessionsLoading: false,
      sessionId: null,
      turns: [],
      chatMode: 'default',
      modeContext: null,
      autoGreetArmed: false,
      stashedSessionId: null,
      stashedTurns: [],
      walkActive: false,
      walkUniverse: null,
      ...initialStreamingState,
      error: null,

      loadSessions: async () => {
        set({ sessionsLoading: true })
        try {
          const sessions = await listConciergeSessions()
          // Backend already orders pinned-first; sort defensively so the UI is
          // correct even if that ordering ever changes.
          set({ sessions: sortPinnedFirst(sessions), sessionsLoading: false })
        } catch (e) {
          // Network failure shouldn't block the chat — just log and leave
          // the previous (possibly stale) list intact.
          console.error('[concierge] loadSessions failed', e)
          set({ sessionsLoading: false })
        }
      },

      togglePin: async (sessionId: string) => {
        const cur = get().sessions
        const target = cur.find((s) => s.session_id === sessionId)
        if (!target) return
        const next = !target.pinned
        // Optimistic: flip + re-sort immediately so the row jumps to/from the
        // pinned group without waiting on the round-trip.
        set({
          sessions: sortPinnedFirst(
            cur.map((s) => (s.session_id === sessionId ? { ...s, pinned: next } : s))
          ),
        })
        try {
          await updateConciergeSession(sessionId, { pinned: next })
        } catch (e) {
          // Revert on failure — reload from the server as source of truth.
          console.error('[concierge] togglePin failed', e)
          await get().loadSessions()
        }
      },

      loadSession: async (sessionId: string) => {
        // Snapshot the relevant store state at request time. We use these
        // as guards before committing the response — if the user has
        // since entered a different mode / different block (e.g. clicked
        // the calendar wrap-up link mid-fetch), the response is stale
        // and committing it would smash the fresh state.
        //
        // Concrete failure mode this fixes (2026-05-26): user clicks a
        // calendar wrap-up link → URL params drive enterMode('wrapup',
        // {today's block}) → enterMode resets sessionId/turns. But
        // ConciergeChat's mount effect fired FIRST with the persisted
        // (stale) sessionId, kicked off this loadSession async, and when
        // the response landed it overwrote enterMode's fresh state with
        // YESTERDAY'S session turns. The wrap-up modal then showed turns
        // from a wrap-up of a different block, confusing the agent on
        // its first response (it would start writing about the wrong
        // block based on the stale chat history).
        const requestedSessionId = sessionId
        const modeAtRequest = get().chatMode
        const blockAtRequest = get().modeContext?.blockPath ?? null
        // Optimistically commit the selected sessionId BEFORE the fetch. The
        // picker calls loadSession(id) without setting sessionId first, so
        // without this Guard 2 below (`now.sessionId !== requestedSessionId`)
        // always fires and EVERY session switch gets dropped (regression from
        // 3fd5a8d). Setting it here makes the guard validate the happy path
        // (now.sessionId === requested → commit) while still dropping genuine
        // races: if the user picks another session or enterMode resets it
        // mid-fetch, sessionId changes again and the stale response is dropped.
        set({ sessionId: requestedSessionId })
        try {
          const detail: ConciergeSessionDetail = await getConciergeSession(sessionId)
          // Guard 1: did the user switch modes (or blocks within a mode)
          // while we were fetching? If so, drop the response on the
          // floor — fresh state wins.
          const now = get()
          if (now.chatMode !== modeAtRequest) return
          if ((now.modeContext?.blockPath ?? null) !== blockAtRequest) return
          // Guard 2: enterMode (or newSession) changed sessionId after we
          // kicked off — covers both "reset to null" (e.g. calendar deep-link
          // → enterMode wipes sessionId) and "swapped to a different session"
          // (e.g. user picked another session mid-fetch). Either case, drop.
          if (now.sessionId !== requestedSessionId) return
          const turns: Turn[] = (detail.turns || []).map((t) => ({
            role: t.role,
            content: t.content,
            timestamp: Date.parse(t.timestamp) || Date.now(),
            thinking: t.thinking || undefined,
            // node_outputs may carry tool call records — we don't surface
            // those from history (just the visible text). Live tool pills
            // appear in real time during streaming and then disappear; the
            // backing block_query/etc. results live in node_outputs on
            // disk for audit but the chat history shows the agent's text
            // response only.
          }))
          set({
            sessionId,
            turns,
            ...initialStreamingState,
            error: null,
          })
        } catch (e) {
          console.error('[concierge] loadSession failed', e)
          set({ error: e instanceof Error ? e.message : 'Failed to load session' })
        }
      },

      newSession: () => {
        set({
          sessionId: null,
          turns: [],
          ...initialStreamingState,
          error: null,
        })
      },

      enterMode: (mode, context) => {
        const s = get()
        // Stash the default-chat state so exitMode() restores it.
        // Never overwrite an existing stash (e.g. mode-to-mode switch)
        // because the stashed copy belongs to the LAST default session,
        // not to the mode we're leaving. The intermediate mode's session
        // is intentionally discarded.
        const stash =
          s.chatMode === 'default'
            ? { stashedSessionId: s.sessionId, stashedTurns: s.turns }
            : { stashedSessionId: s.stashedSessionId, stashedTurns: s.stashedTurns }
        set({
          chatMode: mode,
          modeContext: context,
          // Arm the auto-greet for THIS intentional entry. A rehydrated
          // mode (page reload) never calls enterMode, so it stays disarmed
          // and won't auto-run a stale block's wrap-up.
          autoGreetArmed: true,
          // Each mode mints its own backend session via lazy-create on
          // the first POST, so we start with null + empty turns. The
          // wrap-up workflow auto-greets on an empty-content first POST
          // (per OVERVIEW.md), so the chat won't feel blank for long.
          sessionId: null,
          turns: [],
          ...initialStreamingState,
          error: null,
          ...stash,
        })
      },

      exitMode: () => {
        const s = get()
        // Restore the stashed default session if we have one. Otherwise
        // fall back to a brand-new default chat — same as newSession().
        set({
          chatMode: 'default',
          modeContext: null,
          autoGreetArmed: false,
          sessionId: s.stashedSessionId,
          turns: s.stashedTurns,
          stashedSessionId: null,
          stashedTurns: [],
          walkActive: false,
          walkUniverse: null,
          ...initialStreamingState,
          error: null,
        })
      },

      disarmAutoGreet: () => set({ autoGreetArmed: false }),

      // Wrap-up walks. `startWalk` resolves the first attention-queue
      // block via /walks/start, enters wrapup mode for it, and arms
      // `walkActive` so the chat's onToolResult callback knows to chain
      // to the next block after a terminal action (archive_block /
      // capture_block_outcome / reschedule_block). `advanceWalk` is what
      // the chat calls after detecting that terminal action — it pulls
      // /walks/next and enters wrapup mode for the next block (or shows
      // a "vault cleared" toast and exits mode).
      startWalk: async (universe?: string) => {
        try {
          const walk = await startConciergeWalk(universe)
          if (walk.kind === 'vault_cleared') {
            set({ error: '✓ All clear — nothing needs attention.' })
            return
          }
          if (walk.kind === 'error') {
            set({ error: walk.error })
            return
          }
          const nb = walk.next_block
          // Reuse enterMode for the state transition (handles stashing
          // the default-mode session for exitMode() to restore later).
          get().enterMode('wrapup', {
            blockPath: nb.path,
            projectPath: nb.path
              .split('/')
              .slice(0, -1)
              .join('/') || undefined,
            displayTitle: nb.title || nb.name,
            displaySubtitle: nb.location || undefined,
          })
          // Capture the universe filter so subsequent advances stay
          // constrained to it. Empty string from the caller means "no
          // filter, walk across universes" — normalize to null.
          set({
            walkActive: true,
            walkUniverse: universe && universe.length > 0 ? universe : null,
          })
        } catch (e) {
          console.error('[concierge] startWalk failed', e)
          set({
            error:
              e instanceof Error ? `Start walk: ${e.message}` : 'Start walk failed',
          })
        }
      },

      advanceWalk: async () => {
        const s = get()
        const current = s.modeContext?.blockPath
        if (!current) {
          // No active block to walk from — caller misuse. Treat as no-op.
          return
        }
        try {
          // Persist the universe filter from startWalk through every advance.
          // Without this, /walks/next walks across universe boundaries even
          // when the original startWalk was universe-constrained.
          const walk = await getNextConciergeWalk(current, s.walkUniverse ?? undefined)
          if (walk.kind === 'vault_cleared') {
            set({ error: '✓ All clear — nothing else needs attention.' })
            get().exitMode()
            return
          }
          if (walk.kind === 'error') {
            set({ error: walk.error })
            return
          }
          const nb = walk.next_block
          // enterMode resets sessionId + turns (fresh wrapup session for
          // the next block) but preserves the stashed default-mode state.
          get().enterMode('wrapup', {
            blockPath: nb.path,
            projectPath: nb.path
              .split('/')
              .slice(0, -1)
              .join('/') || undefined,
            displayTitle: nb.title || nb.name,
            displaySubtitle: nb.location || undefined,
          })
          set({ walkActive: true })
        } catch (e) {
          console.error('[concierge] advanceWalk failed', e)
          set({
            error:
              e instanceof Error
                ? `Advance walk: ${e.message}`
                : 'Advance walk failed',
          })
        }
      },

      appendUserTurn: (content: string) => {
        set((s) => ({
          turns: [
            ...s.turns,
            { role: 'user', content, timestamp: Date.now() },
          ],
        }))
      },

      startStreaming: () => {
        set({
          workflowStatus: 'streaming',
          streamingThinking: '',
          streamingToolCalls: [],
          streamingContent: '',
          error: null,
        })
      },

      appendThinking: (delta) => {
        set((s) => ({ streamingThinking: s.streamingThinking + delta }))
      },

      finishThinking: (final) => {
        // If the backend sent a final block, prefer it (it's authoritative);
        // otherwise keep the accumulated stream.
        if (final !== undefined) {
          set({ streamingThinking: final })
        }
      },

      registerToolCall: (call) => {
        set((s) => ({
          streamingToolCalls: [
            ...s.streamingToolCalls,
            {
              // Client-assigned id — no id on the SSE wire. Tool name +
              // position is unique within a single in-flight turn.
              id: `${call.name}-${s.streamingToolCalls.length}`,
              name: call.name,
              input: call.input || {},
              status: 'running',
            },
          ],
        }))
      },

      registerToolResult: (result) => {
        // No id on the wire — pair to the FIRST still-running call with a
        // matching name. This is order-preserving because the backend
        // emits call then result sequentially in the tool loop. If the
        // agent ever runs the same tool name in parallel within one turn,
        // pairing by name+position still works as long as results come
        // back in the same order calls were made (Anthropic's API
        // guarantees this).
        set((s) => {
          const idx = s.streamingToolCalls.findIndex(
            (c) => c.name === result.name && c.status === 'running'
          )
          if (idx === -1) return s
          const next = [...s.streamingToolCalls]
          next[idx] = {
            ...next[idx],
            summary: result.summary,
            status: result.ok ? 'success' : 'error',
          }
          return { streamingToolCalls: next }
        })
      },

      appendStreamingContent: (delta) => {
        set((s) => ({ streamingContent: s.streamingContent + delta }))
      },

      finishStreaming: ({ content, sessionId: newSessionId }) => {
        const s = get()
        // Pin the streamed pieces into a stable assistant turn.
        const assistantTurn: Turn = {
          role: 'assistant',
          content: content || s.streamingContent,
          timestamp: Date.now(),
          thinking: s.streamingThinking || undefined,
          toolCalls: s.streamingToolCalls.length ? s.streamingToolCalls : undefined,
        }
        set({
          turns: [...s.turns, assistantTurn],
          sessionId: newSessionId || s.sessionId,
          ...initialStreamingState,
        })
      },

      setError: (err) => {
        set({ error: err, workflowStatus: err ? 'error' : 'idle' })
      },

      reset: () => {
        set({
          sessionId: null,
          turns: [],
          ...initialStreamingState,
          error: null,
        })
      },
    }),
    {
      name: CONCIERGE_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Persist the active session + its turns AND the current mode so
      // a refresh keeps the user in whatever room they were in (e.g.
      // mid-wrapup → reload → still in wrapup with the same context).
      // Streaming state, the sessions list, and transient errors live
      // in memory; they get refetched / recomputed on each mount.
      partialize: (state) => ({
        sessionId: state.sessionId,
        turns: state.turns,
        chatMode: state.chatMode,
        // `autoRun` is a one-shot intent owned by the calendar-link entry —
        // strip it before persisting so a rehydrated wrap-up can never
        // auto-fire HEADLESS (silent outcome-write + archive) against a
        // stale block after a reload. `autoGreetArmed` is in-memory only
        // (omitted here) for the same reason — only an in-session enterMode
        // re-arms the greet.
        modeContext: state.modeContext
          ? { ...state.modeContext, autoRun: undefined }
          : null,
        stashedSessionId: state.stashedSessionId,
        stashedTurns: state.stashedTurns,
        walkActive: state.walkActive,
        walkUniverse: state.walkUniverse,
      }),
    }
  )
)
